import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft } from 'lucide-react'
import { peekCredentials } from '@/lib/external-credentials-cache'

export const Route = createFileRoute('/settings/custom-modules/$key')({
  component: CustomModuleEditorLayout,
})

function CustomModuleEditorLayout() {
  const { key } = Route.useParams()
  const location = useLocation()
  const t = useTranslations()

  const isExternal = key.startsWith('external:')
  const connectionId = isExternal ? key.slice('external:'.length) : ''
  const hasFreshCreds = isExternal && Boolean(peekCredentials(connectionId))

  const tabs: Array<{ label: string; path: string; match: (p: string) => boolean }> = isExternal
    ? [
        ...(hasFreshCreds || location.pathname.endsWith('/credentials')
          ? [
              {
                label: t('customModules.tabs.credentials'),
                path: `/settings/custom-modules/${key}/credentials`,
                match: (p: string) => p.endsWith(`/${key}/credentials`),
              },
            ]
          : []),
        {
          label: t('customModules.tabs.manifest'),
          path: `/settings/custom-modules/${key}/manifest`,
          match: (p) => p.endsWith(`/${key}/manifest`),
        },
        {
          label: t('customModules.tabs.disconnect'),
          path: `/settings/custom-modules/${key}/disconnect`,
          match: (p) => p.endsWith(`/${key}/disconnect`),
        },
      ]
    : [
        {
          label: 'Overview',
          path: `/settings/custom-modules/${key}`,
          match: (p) => p === `/settings/custom-modules/${key}`,
        },
        {
          label: 'Entities',
          path: `/settings/custom-modules/${key}/entities`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/entities`),
        },
        {
          label: 'Workflows',
          path: `/settings/custom-modules/${key}/workflows`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/workflows`),
        },
        {
          label: 'Webhooks',
          path: `/settings/custom-modules/${key}/webhooks`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/webhooks`),
        },
        {
          label: 'Nav',
          path: `/settings/custom-modules/${key}/nav`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/nav`),
        },
        {
          label: 'Permissions',
          path: `/settings/custom-modules/${key}/permissions`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/permissions`),
        },
        {
          label: 'Audit log',
          path: `/settings/custom-modules/${key}/audit`,
          match: (p) => p.startsWith(`/settings/custom-modules/${key}/audit`),
        },
      ]

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl space-y-4">
      <Link
        to="/settings/custom-modules"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t('customModules.back')}
      </Link>

      <div className="border-b">
        <nav className="flex gap-6 -mb-px">
          {tabs.map((tab) => {
            const isActive = tab.match(location.pathname)
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`pb-3 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </Link>
            )
          })}
        </nav>
      </div>

      <Outlet />
    </div>
  )
}
